import React, { useEffect, useState } from "react";
import { FloppyDisk, ArrowCounterClockwise, Plus, TrashSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";

const AI_LIMIT_ROWS = [
  { key: "tts_chars_per_day", label: "TTS characters / day" },
  { key: "tts_max_chars_per_request", label: "TTS max characters / request" },
  { key: "voiceovers_per_day", label: "AI voiceover renders / day" },
  { key: "ai_artwork_per_day", label: "AI gift artwork / day" },
  { key: "announcements_per_day", label: "Live announcements / day" },
  { key: "ai_videos_per_day", label: "AI text-to-videos / day" },
  { key: "ai_songs_per_day", label: "AI songs / day" },
  { key: "ai_song_max_seconds", label: "AI song max length (sec)" },
];

/** AdminSettings — revenue split, gift prices, gift-creator rules and AI usage limits (all editable). */
export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newOverride, setNewOverride] = useState({ gift_id: "", price: "" });

  const load = async () => {
    try {
      const [{ data }, { data: cat }] = await Promise.all([api.get("/admin/settings"), api.get("/gifts/catalog")]);
      setS(data);
      setCatalog(cat.gifts || []);
    } catch (e) { toast.error("Could not load settings"); }
  };
  useEffect(() => { load(); }, []);

  const setHost = (v) => {
    const h = Math.max(0, Math.min(100, Number(v) || 0));
    setS({ ...s, host_share_pct: h, platform_share_pct: 100 - h });
  };
  const setAi = (k, v) => setS({ ...s, ai_limits: { ...s.ai_limits, [k]: Math.max(0, Number(v) || 0) } });
  const setGc = (k, v) => setS({ ...s, gift_creator: { ...s.gift_creator, [k]: k === "agreement_version" ? v : Math.max(0, Number(v) || 0) } });
  const setFraud = (k, v) => setS({ ...s, fraud: { ...s.fraud, [k]: Math.max(0, Number(v) || 0) } });
  const setLive = (k, v, float = false) => setS({ ...s, live: { ...(s.live || {}), [k]: float ? (Number(v) || 0) : Math.max(0, Number(v) || 0) } });
  const setWeight = (k, v) => setS({ ...s, live: { ...(s.live || {}), weights: { ...((s.live || {}).weights || {}), [k]: Math.max(0, Number(v) || 0) } } });

  const addOverride = () => {
    const gid = newOverride.gift_id.trim();
    const price = Number(newOverride.price);
    if (!gid || !price) return toast.error("Pick a gift and a price");
    setS({ ...s, gift_price_overrides: { ...(s.gift_price_overrides || {}), [gid]: price } });
    setNewOverride({ gift_id: "", price: "" });
  };
  const removeOverride = (gid) => {
    const o = { ...(s.gift_price_overrides || {}) };
    delete o[gid];
    setS({ ...s, gift_price_overrides: o });
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        platform_share_pct: Number(s.platform_share_pct), host_share_pct: Number(s.host_share_pct),
        creator_reward_pct: Number(s.creator_reward_pct || 0),
        gift_price_overrides: s.gift_price_overrides || {}, gift_creator: s.gift_creator, ai_limits: s.ai_limits, fraud: s.fraud,
        live: s.live,
      };
      const { data } = await api.put("/admin/settings", body);
      setS(data);
      toast.success("Settings saved — live immediately");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const resetDefaults = () => {
    if (!window.confirm("Reset the form to platform defaults? (Nothing is saved until you press Save.)")) return;
    setS({ ...s, ...s.defaults });
  };

  if (!s) return <p className="text-cream/40 text-xs tracking-[0.3em] uppercase animate-pulse">Loading…</p>;

  return (
    <div data-testid="admin-settings" className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-cream/60 text-xs tracking-[0.25em] uppercase font-bold">Revenue & limits</p>
        {s.updated_at && <p className="text-cream/40 text-[10px]">Last saved {new Date(s.updated_at).toLocaleString()}</p>}
      </div>

      {/* Revenue split */}
      <Card title="Revenue split" testid="admin-settings-split" hint="Applies to every gift, live gift, paid room and subscription from now on. Creator reward is carved out of the platform share.">
        <div className="grid grid-cols-3 gap-3">
          <Num label="Host / creator %" value={s.host_share_pct} onChange={setHost} testid="admin-split-host" />
          <Num label="Platform %" value={s.platform_share_pct} onChange={(v) => setHost(100 - (Number(v) || 0))} testid="admin-split-platform" />
          <Num label="Gift designer reward %" value={s.creator_reward_pct || 0} onChange={(v) => setS({ ...s, creator_reward_pct: Math.max(0, Math.min(100, Number(v) || 0)) })} testid="admin-split-designer" />
        </div>
        <div className="mt-3 h-3 rounded-full overflow-hidden flex bg-white/10" data-testid="admin-split-bar">
          <div className="bg-papaya h-full transition-all" style={{ width: `${s.host_share_pct}%` }} />
          <div className="bg-jam-gold h-full transition-all" style={{ width: `${s.creator_reward_pct || 0}%` }} />
          <div className="bg-white/30 h-full flex-1" />
        </div>
        <p className="text-cream/50 text-[11px] mt-1.5">100 waves gifted → host {s.host_share_pct} 🪩 · designer {s.creator_reward_pct || 0} 🪩 · platform keeps {Math.max(0, s.platform_share_pct - (s.creator_reward_pct || 0))} 🪙</p>
      </Card>

      {/* Gift prices */}
      <Card title="Gift price overrides" testid="admin-settings-prices" hint="Override the catalog price (in waves) for any official gift. Marketplace gifts are priced on approval.">
        <div className="space-y-1.5">
          {Object.entries(s.gift_price_overrides || {}).map(([gid, price]) => {
            const g = catalog.find((x) => x.id === gid);
            return (
              <div key={gid} className="flex items-center gap-2 p-2 rounded-2xl bg-white/5 border border-white/5" data-testid={`admin-price-override-${gid}`}>
                <span className="text-lg">{g?.emoji || "🎁"}</span>
                <span className="flex-1 text-cream text-sm font-bold truncate">{g?.label || gid} <span className="text-cream/40 text-[10px] font-normal">{gid}</span></span>
                <input type="number" min={1} value={price} onChange={(e) => setS({ ...s, gift_price_overrides: { ...s.gift_price_overrides, [gid]: Number(e.target.value) || 0 } })} className={`${inpBase} w-24 flex-none py-1.5`} data-testid={`admin-price-input-${gid}`} />
                <button onClick={() => removeOverride(gid)} data-testid={`admin-price-remove-${gid}`} className="w-8 h-8 rounded-full bg-red-500/15 text-red-300 flex items-center justify-center"><TrashSimple size={13} /></button>
              </div>
            );
          })}
          {Object.keys(s.gift_price_overrides || {}).length === 0 && <p className="text-cream/40 text-xs">No overrides — catalog defaults apply.</p>}
        </div>
        <div className="flex gap-2 mt-3">
          <select value={newOverride.gift_id} onChange={(e) => setNewOverride({ ...newOverride, gift_id: e.target.value })} className={`${inpBase} flex-1 min-w-0`} data-testid="admin-price-gift-select">
            <option value="">Choose a gift…</option>
            {catalog.filter((g) => !g.custom).map((g) => <option key={g.id} value={g.id}>{`${g.emoji} ${g.label} · ${g.waves} waves`}</option>)}
          </select>
          <input type="number" min={1} value={newOverride.price} onChange={(e) => setNewOverride({ ...newOverride, price: e.target.value })} placeholder="Waves" className={`${inpBase} w-24 flex-none`} data-testid="admin-price-new" />
          <button onClick={addOverride} data-testid="admin-price-add" className="px-3 rounded-2xl bg-papaya text-ink flex items-center justify-center"><Plus size={16} weight="bold" /></button>
        </div>
      </Card>

      {/* Gift creator rules */}
      <Card title="Gift Creator rules" testid="admin-settings-giftcreator" hint="Designers pay the creation fee on every submission (non-refundable) — it goes straight to you. Designer reward defaults to 0%.">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Num label="Min price (waves)" value={s.gift_creator.min_price_waves} onChange={(v) => setGc("min_price_waves", v)} testid="admin-gc-min" />
          <Num label="Default price" value={s.gift_creator.default_price_waves} onChange={(v) => setGc("default_price_waves", v)} testid="admin-gc-default" />
          <Num label="Max price" value={s.gift_creator.max_price_waves} onChange={(v) => setGc("max_price_waves", v)} testid="admin-gc-max" />
          <Num label="Submissions / day" value={s.gift_creator.submissions_per_day} onChange={(v) => setGc("submissions_per_day", v)} testid="admin-gc-subs" />
          <Num label="Creation fee (waves, you keep)" value={s.gift_creator.creation_fee_waves ?? 0} onChange={(v) => setGc("creation_fee_waves", v)} testid="admin-gc-fee" />
          <label className="block">
            <span className="text-cream/60 text-[10px] tracking-[0.2em] uppercase font-bold">Agreement version</span>
            <input value={s.gift_creator.agreement_version} onChange={(e) => setGc("agreement_version", e.target.value)} className={`${inp} mt-1`} data-testid="admin-gc-agreement" />
          </label>
        </div>
      </Card>

      {/* AI limits */}
      <Card title="AI usage limits" testid="admin-settings-ai" hint="Free = everyone. Premium = Voice Pro or VIP subscribers.">
        <div className="grid grid-cols-[1fr_88px_88px] gap-2 items-center">
          <span className="text-cream/40 text-[10px] tracking-[0.2em] uppercase font-bold">Limit</span>
          <span className="text-cream/40 text-[10px] tracking-[0.2em] uppercase font-bold text-center">Free</span>
          <span className="text-jam-gold text-[10px] tracking-[0.2em] uppercase font-bold text-center">Premium</span>
          {AI_LIMIT_ROWS.map((r) => (
            <React.Fragment key={r.key}>
              <span className="text-cream text-sm">{r.label}</span>
              <input type="number" min={0} value={s.ai_limits[`${r.key}_free`] ?? 0} onChange={(e) => setAi(`${r.key}_free`, e.target.value)} className={`${inp} py-1.5 text-center`} data-testid={`admin-ai-${r.key}-free`} />
              <input type="number" min={0} value={s.ai_limits[`${r.key}_premium`] ?? 0} onChange={(e) => setAi(`${r.key}_premium`, e.target.value)} className={`${inp} py-1.5 text-center border-jam-gold/30`} data-testid={`admin-ai-${r.key}-premium`} />
            </React.Fragment>
          ))}
        </div>
      </Card>

      {/* Fraud */}
      <Card title="Anti-fraud thresholds" testid="admin-settings-fraud" hint="Accounts crossing these get flagged in the Gifts tab for review / wallet freeze.">
        <div className="grid grid-cols-2 gap-3">
          <Num label="Max gifts / minute" value={s.fraud.max_gifts_per_minute} onChange={(v) => setFraud("max_gifts_per_minute", v)} testid="admin-fraud-gpm" />
          <Num label="Max waves / 10 min" value={s.fraud.max_waves_per_10min} onChange={(v) => setFraud("max_waves_per_10min", v)} testid="admin-fraud-w10" />
        </div>
      </Card>

      {/* LIVE ranking & leagues */}
      {s.live && (
        <Card title="LIVE ranking & leagues" testid="admin-settings-live" hint="Controls the engagement score, the D1→A7 league ladder and gift-goal defaults. Applies to every live from now on.">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Num label="Ranking window (min)" value={s.live.ranking_window_min} onChange={(v) => setLive("ranking_window_min", v)} testid="admin-live-window" />
            <Num label="League base points" value={s.live.league_base} onChange={(v) => setLive("league_base", v)} testid="admin-live-base" />
            <label className="block">
              <span className="text-cream/60 text-[10px] tracking-[0.2em] uppercase font-bold">League growth (×/tier)</span>
              <input type="number" step="0.05" min={1.01} value={s.live.league_growth} onChange={(e) => setLive("league_growth", e.target.value, true)} className={`${inp} mt-1`} data-testid="admin-live-growth" />
            </label>
            <Num label="Divisions / league" value={s.live.divisions} onChange={(v) => setLive("divisions", v)} testid="admin-live-divisions" />
            <Num label="Gift goal default (waves)" value={s.live.gift_goal_default_waves} onChange={(v) => setLive("gift_goal_default_waves", v)} testid="admin-live-goal-waves" />
            <Num label="Gift goal default (min)" value={s.live.gift_goal_default_minutes} onChange={(v) => setLive("gift_goal_default_minutes", v)} testid="admin-live-goal-min" />
          </div>
          <p className="text-cream/40 text-[11px] mt-3 mb-1">Leagues: <span className="text-cream/70 font-bold">{(s.live.leagues || []).join(" · ")}</span> (low → high, top tier = {(s.live.leagues || ["A"]).slice(-1)[0]}{s.live.divisions})</p>
          <p className="text-cream/50 text-[10px] tracking-[0.2em] uppercase font-bold mt-3 mb-2">Engagement weights</p>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {["waves", "viewers", "gifts", "chat", "follows"].map((k) => (
              <label key={k} className="block">
                <span className="text-cream/60 text-[9px] tracking-[0.15em] uppercase font-bold">{k}</span>
                <input type="number" min={0} value={(s.live.weights || {})[k] ?? 0} onChange={(e) => setWeight(k, e.target.value)} className={`${inp} py-1.5 mt-1 text-center`} data-testid={`admin-live-weight-${k}`} />
              </label>
            ))}
          </div>
        </Card>
      )}

      <div className="flex gap-2 sticky bottom-24 z-20 p-2 rounded-full bg-ink/85 backdrop-blur-md border border-white/10">
        <button onClick={resetDefaults} data-testid="admin-settings-reset" className="px-4 py-3.5 rounded-full bg-white/5 border border-white/15 text-cream text-[10px] font-black tracking-[0.2em] uppercase flex items-center gap-1.5"><ArrowCounterClockwise size={14} weight="bold" /> Defaults</button>
        <button onClick={save} disabled={busy} data-testid="admin-settings-save" className="flex-1 py-3.5 rounded-full bg-papaya text-ink text-[10px] font-black tracking-[0.2em] uppercase flex items-center justify-center gap-1.5 disabled:opacity-40"><FloppyDisk size={14} weight="bold" /> {busy ? "Saving…" : "Save settings"}</button>
      </div>
    </div>
  );
}

const inpBase = "bg-white/5 border border-white/10 rounded-2xl px-3 py-2.5 text-cream placeholder:text-cream/30 text-sm focus:outline-none focus:border-papaya";
const inp = `w-full ${inpBase}`;

function Card({ title, hint, children, testid }) {
  return (
    <section className="rounded-3xl bg-white/5 border border-white/10 p-4" data-testid={testid}>
      <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-black">{title}</p>
      {hint && <p className="text-cream/40 text-[11px] mt-0.5 mb-3">{hint}</p>}
      {!hint && <div className="mb-3" />}
      {children}
    </section>
  );
}

function Num({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-cream/60 text-[10px] tracking-[0.2em] uppercase font-bold">{label}</span>
      <input type="number" min={0} value={value} onChange={(e) => onChange(e.target.value)} className={`${inp} mt-1`} data-testid={testid} />
    </label>
  );
}
